import { each, includes, isEmpty, mapValues, set } from "lodash";
import validate from "validate.js";
import listingValidation from "../../listing/ducks/validation";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

const compConstraints = {
    Office: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Industrial: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.largestContigSf": {
            ...constraints.number,
        },
        "listing.attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "listing.attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "listing.attributes.blendedRate": {
            ...constraints.number,
        },
        "listing.attributes.ampsNum": {
            ...constraints.number,
        },
        "listing.attributes.phaseNum": {
            ...constraints.number,
        },
        "listing.attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    "Life Science": {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Retail: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Land: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Multifamily: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Hospitality: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.hospitalitySf": {
            ...constraints.id,
        },
        "listing.attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "listing.attributes.roomCount": {
            ...constraints.id,
        },
    },
    Other: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Educational: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.classroomCount": {
            ...constraints.number,
        },
        "listing.attributes.auditoriumSeatsCount": {
            ...constraints.number,
        },
    },
    Healthcare: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    totalConsideration: {
        commencementDate: {
            ...constraints.required,
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.required,
            ...constraints.date,
        },
        termNumMonths: {
            ...constraints.required,
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.required,
            ...constraints.number,
        },
        escalationRate: {
            ...constraints.required,
            ...constraints.number,
        },
        escalationTerm: {
            ...constraints.required,
            ...constraints.number,
        },
        totalConsiderationAmount: {
            ...constraints.required,
            ...constraints.number,
        },
    },
    listingInformation: {
        "listing.dateListed": {
            ...constraints.date,
        },
        "listing.dateAvailable": {
            ...constraints.date,
        },
        "listing.expirationDate": {
            ...constraints.date,
        },
        "listing.leaseExpirationDate": {
            ...constraints.date,
        },
        "listing.attributes.numBldg": {
            ...constraints.number,
        },
        "listing.attributes.lotAcres": {
            ...constraints.number,
        },
        "listing.attributes.lotSf": {
            ...constraints.number,
        },
    },
    listingSellerLandlord: {
        "listing.sellerLandlordLegalCompanyName": {
            ...constraints.maxLength,
        },
        "listing.sellerLandlordCommonCompanyName": {
            ...constraints.maxLength,
        },
    },
    listingInformationSf: {
        "listing.sfAvail": {
            ...constraints.required,
            ...constraints.number,
        },
    },
    leaseDetails: {
        tiRate: {
            ...constraints.number,
            ...constraints.between0And1000,
            ...constraints.required,
        },
        leaseType: {
            ...constraints.required,
        },
        movingAllowanceAmount: {
            ...constraints.number,
        },
        naicsCode: {
            ...constraints.id,
        },
        sicCode: {
            ...constraints.id,
        },
        currentSf: {
            ...constraints.number,
        },
        squareFeetChange: {
            ...constraints.number,
        },
    },
    Lease: {
        //comp information
        //required fields
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.required,
            ...constraints.date,
        },
        totalKmCommission: {
            ...constraints.required,
            ...constraints.number,
        },
        representationType: {
            ...constraints.required,
        },
        renewalFlg: {
            ...constraints.required,
        },
        spaceType: {
            ...constraints.required,
        },
        //other constraints
        buyerTenantCommonCompanyName: {
            ...constraints.required,
        },
        buyerTenantLegalCompanyName: {
            ...constraints.required,
        },
        sellerLandlordLegalCompanyName: {
            ...constraints.required,
        },
        sellerLandlordCommonCompanyName: {
            ...constraints.required,
        },
        outsideBrokerCommission: {
            ...constraints.number,
        },
    },
    Sale: {
        //comp information
        //required fields
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.required,
            ...constraints.date,
        },
        closedDate: {
            ...constraints.required,
            ...constraints.date,
        },
        totalKmCommission: {
            ...constraints.required,
            ...constraints.number,
        },
        leaseBackFlg: {
            ...constraints.required,
        },
        representationType: {
            ...constraints.required,
        },
        totalConsiderationAmount: {
            ...constraints.required,
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.required,
            ...constraints.number,
        },
        buyerTenantCommonCompanyName: {
            ...constraints.required,
        },
        buyerTenantLegalCompanyName: {
            ...constraints.required,
        },
        sellerLandlordLegalCompanyName: {
            ...constraints.required,
        },
        sellerLandlordCommonCompanyName: {
            ...constraints.required,
        },
    },
    saleSpaceType: {
        spaceType: {
            ...constraints.required,
        },
    },
    parkingInformation: {
        "listing.attributes.parkingTotal": {
            ...constraints.number,
        },
        "listing.attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
    },
    listingFinancial: {
        "listing.leaseType": {
            ...constraints.required,
        },
        "listing.improvementAllowance": {
            ...constraints.number,
        },
        "listing.askingRentMax": {
            ...constraints.required,
            ...constraints.number,
        },
        "listing.askingRentPerMonth": {
            ...constraints.required,
            ...constraints.number,
        },
        "listing.baseRentPerYear": {
            ...constraints.required,
            ...constraints.number,
        },
        "listing.nnnPerMonth": {
            ...constraints.number,
        },
        "listing.nnnPerYear": {
            ...constraints.number,
        },
        "listing.totalMonthlyRent": {
            ...constraints.number,
        },
        "listing.totalRentPerYear": {
            ...constraints.number,
        },
    },
    saleFinancial: {
        "listing.listPrice": {
            ...constraints.required,
            ...constraints.number,
        },
    },
    sellerLandlordCompany: {
        sellerLandlordCommonCompanyName: {
            ...constraints.required,
        },
        sellerLandlordLegalCompanyName: {
            ...constraints.required,
        },
    },
    companyAdminConstraints: {
        sellerLandlordCompany: {
            ...constraints.required,
        },
        buyerTenantCompany: {
            ...constraints.required,
        },
    },
    annualRentCalculationConstraints: {
        ratePerYear: {
            ...constraints.required,
            ...constraints.number,
        },
    },
    montlyRentCalculationConstraints: {
        ratePerSqftPerMonth: {
            ...constraints.required,
            ...constraints.number,
        },
        ratePerSqftPerYear: {
            ...constraints.required,
            ...constraints.number,
        },
        ratePerMonth: {
            ...constraints.required,
            ...constraints.number,
        },
    },
};

const installmentConstraints = {
    amount: {
        ...constraints.required,
        ...constraints.number,
    },
    dueDate: {
        ...constraints.required,
        ...constraints.date,
    },
};

const validateComp = (comp, useType, transactionType, documents) => {
    let whatToValidate = [];
    switch (transactionType) {
        case "Lease":
            whatToValidate = [
                useType,
                transactionType,
                "leaseDetails",
                "totalConsideration",
                "buildingInformation",
                "parkingInformation",
            ];

            comp.annualRentForCalcFlg
                ? whatToValidate.push("annualRentCalculationConstraints")
                : whatToValidate.push("montlyRentCalculationConstraints");

            break;
        case "Sale":
            whatToValidate = [useType, transactionType, "buildingInformation", "parkingInformation"];

            if (comp.leaseBackFlg === true || comp.leaseBackFlg === "true") {
                whatToValidate.push("saleSpaceType");
            }
            break;
        default:
            break;
    }

    if (["In Review", "Final Review"].includes(comp.dealStatus) && ["Lease", "Sale"].includes(transactionType)) {
        whatToValidate.push("companyAdminConstraints");
    }

    if (comp.fromListing === true) {
        whatToValidate.push("listingInformation");
        if (transactionType === "Lease") {
            whatToValidate.push("listingFinancial");
            whatToValidate.push("listingInformationSf");
        } else if (transactionType === "Sale") {
            whatToValidate.push("saleFinancial");
        }
    }

    whatToValidate.push("listingSellerLandlord");

    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...compConstraints[section] };
    });

    let errors = {
        ...mapValues(validate(comp, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...listingValidation.validateListingBrokersCompany(comp?.listing?.listingBrokers),
        ...listingValidation.validateMixedUses(comp?.listing?.attributes?.mixedUses),
        ...listingValidation.validatePortfolioProperties(comp?.listing?.attributes?.portfolioProperties),
        ...listingValidation.validateCondos(comp?.listing?.attributes?.condos),
        ...listingValidation.validateisDivisible(comp?.listing?.isDivisible, comp?.listing?.sfDivisibleTo),
        ...validateInstallments(comp.installments),
        ...(comp?.compStatus === "Portland Review Required" ? validateRequiredDocuments(documents) : {}),
    };

    if (transactionType === "Lease" && comp.fromListing === true) {
        const autoRenew = listingValidation.validateAutoRenew(comp.listing);
        if (!isEmpty(autoRenew)) {
            errors = {
                ...errors,
                ...{ "listing.expirationDate": autoRenew },
            };
        }
    }
    return errors;
};

const validateRequiredDocuments = (documents) => {
    const requiredDocs = Object.values(documents?.requiredDocs || {}).map((doc) => doc.name);
    const savedDocs = Object.values(documents?.savedDocuments || {}).map((doc) => doc?.documentType.name);
    const missingDocs = requiredDocs.filter((docName) => !savedDocs.includes(docName));
    return missingDocs.length === 0 ? {} : { documents: `${missingDocs[0]}` };
};

const validateInstallments = (installments) => {
    let errors = {};
    if (installments) {
        for (let i = 0; i < installments.length; i++) {
            const validationResponse = validateInstallment(installments[i]);
            if (validationResponse) {
                set(errors, [installments[i].uniqueId], {
                    ...mapValues(validationResponse, (value) => value[0]),
                });
            }
        }
    }
    return isEmpty(errors) ? {} : { installments: errors };
};

const validateInstallment = (installment) => {
    return validate(installment, installmentConstraints, { fullMessages: false });
};

const getPanelsToExpand = (errors, useType, transactionType) => {
    let listingInformationConstraints = {};

    each([useType, "listingInformation", "parkingInformation"], (section) => {
        listingInformationConstraints = { ...listingInformationConstraints, ...compConstraints[section] };
    });

    const otherListingInfo = [
        "attributes.condos",
        "attributes.mixedUses",
        "attributes.portfolioProperties",
        "listing.listingBrokers",
    ];
    const otherCompInfo = ["buyerTenantCompany", "sellerLandlordCompany", "listing.attributes.specialtyType"];

    let compInformationConstraints = { ...compConstraints[transactionType] };
    const panelsToExpand = new Set();
    errors &&
        Object.keys(errors).forEach((key) => {
            if (
                includes(
                    [...Object.keys(compConstraints.listingFinancial), ...Object.keys(compConstraints.saleFinancial)],
                    key
                )
            ) {
                panelsToExpand.add("listingFinancial");
            } else if (
                key !== "listing.attributes.specialtyType" &&
                includes([...Object.keys(listingInformationConstraints), ...otherListingInfo], key)
            ) {
                panelsToExpand.add("listingInformation");
            } else if (
                key !== "totalKmCommission" &&
                includes([...Object.keys(compInformationConstraints), ...otherCompInfo], key)
            ) {
                panelsToExpand.add("compInformation");
            } else if (includes([...Object.keys(compConstraints.totalConsideration)], key)) {
                panelsToExpand.add("totalConsideration");
            } else if (includes([...Object.keys(compConstraints.leaseDetails)], key)) {
                panelsToExpand.add("leaseDetails");
            } else if (includes(["installments", "totalKmCommission"], key)) {
                panelsToExpand.add("installments");
            }
        });

    return [...panelsToExpand];
};

const validateCompByField = (section, id, value, comp) => {
    const sectionsRequiringFullValidation = [
        "listingFinancial",
        "montlyRentCalculationConstraints",
        "annualRentCalculationConstraints",
        "totalConsideration",
    ];
    if (sectionsRequiringFullValidation.includes(section)) {
        const includeMonthlyRent =
            section === "totalConsideration" && id === "squareFeet" && !comp?.annualRentForCalcFlg;
        const sectionConstraints = {
            ...compConstraints[section],
            ...(includeMonthlyRent && compConstraints.montlyRentCalculationConstraints),
        };
        const sectionErrors = validate(comp, sectionConstraints, { fullMessages: false }) || {};
        return Object.keys(sectionConstraints).map((fullKey) => ({
            fullKey,
            resolved: isEmpty(sectionErrors[fullKey]),
        }));
    } else if (section === "installment") {
        const field = id.split(".").pop();
        const { result } = validateFieldWithKey(installmentConstraints, field, value);
        return [{ fullKey: id, resolved: isEmpty(result) }];
    }
    const { fullKey, result } = validateFieldWithKey(compConstraints, id, value, section);
    return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
};

export default {
    validateComp,
    validateCompByField,
    getPanelsToExpand,
    validateInstallments,
};
